import { Component, ElementRef, ViewChild, inject, signal } from '@angular/core'
import { FormBuilder, Validators } from '@angular/forms'
import { EMPTY, Subscription, catchError, finalize, switchMap, tap } from 'rxjs'
import { BaseComponent } from '../../common/components/base.component'
import { OrderService } from '../../common/services/order/order.service'
import { TpvOrderDetails, TpvOrderLogData } from '../../common/models/order/api-models/tpv.model'

const STATUS_CLASSES: Record<string, string> = {
  Expired: 'badge-alert',
  Unlocked: 'badge-alert',
  Approved: 'badge-positive',
  Cancelled: 'badge-negative'
}

@Component({
  selector: 'app-tpv',
  templateUrl: './tpv.component.html',
  styleUrl: './tpv.component.scss',
  standalone: false
})
export class TpvComponent extends BaseComponent {
  #orderService = inject(OrderService)
  #searchSubscription = new Subscription()
  @ViewChild('orderNumberInput') orderNumberInput: ElementRef<HTMLInputElement>

  searchForm = inject(FormBuilder).group({
    orderNumber: ['', Validators.required],
    callerNumber: ['', Validators.pattern(/^\d*$/)]
  })
  searching = signal(false)
  result = signal<TpvOrderLogData | null>(null)
  showCompletedDialog = signal(false)
  details = signal<TpvOrderDetails | null>(null)
  detailsLoading = signal(false)
  detailsError = signal(false)

  readonly STATUS_CLASSES = STATUS_CLASSES

  get statusLabel(): string | null {
    if (this.result()?.SearchStatus === 'Expired') return 'Expired'
    const status = this.details()?.Header?.TpvStatus
    return status in STATUS_CLASSES ? status : null
  }

  search() {
    if (this.searching()) return
    this.searchForm.controls.orderNumber.setValue(this.searchForm.value.orderNumber.trim())
    if (this.searchForm.invalid) {
      this.searchForm.markAllAsTouched()
      this.orderNumberInput.nativeElement.focus()
      return
    }

    this.#searchSubscription.unsubscribe()
    this.result.set(null)
    this.details.set(null)
    this.detailsError.set(false)
    this.searching.set(true)
    this.#searchSubscription = this.#orderService.getTpvOrderLogData(this.searchForm.value.orderNumber).pipe(
      finalize(() => this.searching.set(false)),
      tap(resp => {
        this.searching.set(false)
        this.result.set(resp)
        this.showCompletedDialog.set(resp.SearchStatus === 'Completed')
      }),
      switchMap(resp => {
        if (resp.SearchStatus !== 'Valid' && resp.SearchStatus !== 'Completed') return EMPTY
        this.detailsLoading.set(true)
        return this.#orderService.getTpvOrderDetails(resp.OrderNumber).pipe(
          tap(details => this.details.set(details)),
          catchError(() => {
            this.detailsError.set(true)
            return EMPTY
          }),
          finalize(() => this.detailsLoading.set(false))
        )
      })
    ).subscribe()
    this.subscriptions.add(this.#searchSubscription)
  }
}
